import { createContext, useContext, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import api from "../config/axiosConfig";
import { useAuth } from "./AuthContext";

const AdminContext = createContext();

const initialState = {
  adminProfile: null,
  adminLoading: true,
  adminError: null,
};

const adminReducer = (state, action) => {
  switch (action.type) {
    case "FETCH_ADMIN_PROFILE_REQUEST":
      return { ...state, adminLoading: true, adminError: null };
    case "FETCH_ADMIN_PROFILE_SUCCESS":
      return { ...state, adminProfile: action.payload, adminLoading: false, adminError: null };
    case "FETCH_ADMIN_PROFILE_FAILURE":
      return { ...state, adminProfile: null, adminLoading: false, adminError: action.payload };
    case "CLEAR_ADMIN_PROFILE":
      return { ...state, adminProfile: null, adminLoading: false, adminError: null };
    default:
      return state;
  }
};

export const AdminProvider = ({ children }) => {
  const [state, dispatch] = useReducer(adminReducer, initialState);
  const { isAuthenticated, authLoading, userId, logout } = useAuth();

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const MANAGER_ROLES = [
  "admin",
  "administrateur",
  "entreprise",
  "collectivité",
  "établissement",
  "staff"
];

  const fetchAdminProfile = async () => {
    dispatch({ type: "FETCH_ADMIN_PROFILE_REQUEST" });

    const accessToken = getCookie("access_token");
    if (!accessToken) {
      const error = new Error("Aucun token d'accès trouvé");
      dispatch({ type: "FETCH_ADMIN_PROFILE_FAILURE", payload: error.message });
      toast.error("Session expirée, veuillez vous reconnecter");
      logout();
      return;
    }

    try {
      const response = await api.get(`/admin/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const profileData = response.data?.profile || response.data;
      if (!profileData || !profileData._id) {
        throw new Error("Aucun profil administrateur reçu");
      }

      const formattedProfile = {
        id: profileData._id.toString(),
        nom: profileData.nom || "",
        prenom: profileData.prenom || "",
        email: profileData.email || "",
        phoneNumber: profileData.phoneNumber || "",
        photoUrl: profileData.photoUrl || "",
        role: profileData.role || "admin",
        pesaRole: profileData.pesaRole || "",
        pesas: profileData.pesas ?? 220,
        isPremium: profileData.isPremium ?? false,
        subscription: {
          plan: profileData.subscription?.plan || "freemium",
          startDate: profileData.subscription?.startDate || null,
          endDate: profileData.subscription?.endDate || null,
          status: profileData.subscription?.status || "canceled",
          stripeCustomerId: profileData.subscription?.stripeCustomerId || "",
          stripeSubscriptionId: profileData.subscription?.stripeSubscriptionId || "",
        },
        limits: {
          maxPropositions: profileData.subscription?.plan === "school" || profileData.subscription?.plan === "premium" ? "Infinity" : (profileData.limits?.maxPropositions ?? 5),
          maxNegociations: profileData.subscription?.plan === "school" || profileData.subscription?.plan === "premium" ? "Infinity" : (profileData.limits?.maxNegociations ?? 3),
          maxParcours: profileData.subscription?.plan === "school" || profileData.subscription?.plan === "premium" ? "Infinity" : (profileData.limits?.maxParcours ?? 2),
        },
        createdAt: profileData.createdAt || "",
        updatedAt: profileData.updatedAt || "",
      };

      dispatch({ type: "FETCH_ADMIN_PROFILE_SUCCESS", payload: formattedProfile });
    } catch (err) {
      console.error("Erreur lors de la récupération du profil administrateur:", err);
      dispatch({ type: "FETCH_ADMIN_PROFILE_FAILURE", payload: err.message || "Erreur lors de la récupération du profil administrateur" });
      toast.error(err.message || "Erreur lors de la récupération du profil administrateur");
      if (err.response?.status === 401) {
        logout();
      }
    }
  };

  useEffect(() => {
  if (isAuthenticated && !authLoading && userId) {
    if (MANAGER_ROLES.includes(userId.role)) {
      fetchAdminProfile();
    } else {
      dispatch({ type: "CLEAR_ADMIN_PROFILE" });
    }
  } else if (!isAuthenticated && !authLoading) {
    dispatch({ type: "CLEAR_ADMIN_PROFILE" });
  }
}, [isAuthenticated, authLoading, userId]);

  return (
    <AdminContext.Provider value={{ ...state, fetchAdminProfile }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => useContext(AdminContext);